import { debounce } from "lodash-es";
import React, { useCallback, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";

import { useDispatch, useSelector } from "../../store";
import { StringMap } from "../../store/pomeriumApi";
import { ReduxFormProps } from "../../types/ReduxForm";
import { CircleMinus, CirclePlus } from "../icons";
import TextField from "../styled/TextField";

const StringMapInput: React.FC<ReduxFormProps & { readonly: boolean }> = ({
  getter,
  setter,
  path,
  defaultValue = {},
  readonly,
}) => {
  const dispatch = useDispatch();
  const formValue = useSelector(getter(path, defaultValue)) as StringMap;
  const [map, setMap] = useState<StringMap>(formValue);
  const [open, setOpen] = useState<boolean>(false);
  const [newPropertyKey, setNewPropertyKey] = useState<string>("");
  const [newPropertyValue, setNewPropertyValue] = useState<string>("");

  const onClose = () => {
    setNewPropertyKey("");
    setNewPropertyValue("");
    setOpen(false);
  };

  const addProperty = () => {
    onChange(newPropertyKey, newPropertyValue);
    onClose();
  };

  const delayedOnSave = useCallback(
    debounce((val) => {
      dispatch(setter({ path, value: val }));
    }, 150),
    [setter, path],
  );

  const onChange = (key: string, value: string): void => {
    setMap((prevMap) => {
      const newMap = { ...(prevMap || {}), [key]: value };
      delayedOnSave(newMap);
      return newMap;
    });
  };

  const onBlur = (): void => {
    delayedOnSave(map);
  };

  const handleDelete = (key: string) => {
    const newMap = { ...map };
    delete newMap[key];
    setMap(newMap);
    delayedOnSave(newMap);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        scroll="paper"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Grid container spacing={2}>
            <Grid size={10}>
              <Typography variant="h4">Add New Property</Typography>
            </Grid>
            <Grid size={2}>
              <IconButton
                disabled={readonly}
                color="primary"
                onClick={onClose}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Container disableGutters>
            <Grid container spacing={2} alignItems="stretch">
              <Grid size={12}>
                <Typography variant="body2" gutterBottom>
                  Property Name
                </Typography>
                <TextField
                  disabled={readonly}
                  fullWidth
                  value={newPropertyKey}
                  onChange={(evt): void => {
                    setNewPropertyKey(evt.target.value);
                  }}
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="body2" gutterBottom>
                  Property Value
                </Typography>
                <TextField
                  disabled={readonly}
                  fullWidth
                  value={newPropertyValue}
                  onChange={(evt): void => {
                    setNewPropertyValue(evt.target.value);
                  }}
                />
              </Grid>
            </Grid>
          </Container>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={!newPropertyKey || readonly}
            variant="contained"
            color="primary"
            onClick={addProperty}
          >
            Add Property
          </Button>
        </DialogActions>
      </Dialog>
      <Grid container rowSpacing={1}>
        <Grid container spacing={2}>
          {!!Object.keys(map).length && (
            <Grid size={4}>
              <>
                <Typography variant="body2" gutterBottom>
                  KEY
                </Typography>
                <Divider />
              </>
            </Grid>
          )}
          {!!Object.keys(map).length && (
            <Grid size={6}>
              <>
                <Typography variant="body2" gutterBottom>
                  VALUE
                </Typography>
                <Divider />
              </>
            </Grid>
          )}
          <Grid size={2}>
            <IconButton
              disabled={readonly}
              size="small"
              onClick={(): void => setOpen(true)}
            >
              <CirclePlus color="primary" />
            </IconButton>
          </Grid>
        </Grid>
        {Object.keys(map).map((key) => (
          <Grid key={key} container spacing={2}>
            <Grid size={4}>
              <FormLabel>{key}</FormLabel>
            </Grid>
            <Grid size={6}>
              <TextField
                disabled={readonly}
                fullWidth
                value={map[key]}
                onChange={(e) => onChange(key, e.target.value)}
                onBlur={onBlur}
              />
            </Grid>
            <Grid size={2}>
              <IconButton
                disabled={readonly}
                size="small"
                onClick={(): void => handleDelete(key)}
                sx={{
                  pt: 1,
                }}
              >
                <CircleMinus color="primary" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default StringMapInput;
